/**
 * The Reference data screen (f2.2) -- Admin only.
 *
 * A separate screen rather than a tab inside f2.1's Configuration, because
 * the two are different kinds of thing: Configuration edits a fixed set of
 * declared VALUES, this manages ROWS that other tables point at. Folding
 * them together would put a table with add-a-row inside a form with one Save
 * button, and the two have different save semantics.
 *
 * **The tab strip is built from what the server sent.** One tab per
 * registered lookup type, in registry order, then the two tables that are
 * not lookup types -- Countries, which carries aliases, and the Resource BU
 * mapping. No lookup type is named in this file, so a phase that registers a
 * new type gets a working tab with a working table and changes nothing here.
 *
 * Visibility comes from the server's navigation list. Hiding the screen is
 * presentation only: the SERVER refuses every non-Admin write, and records
 * the refusal.
 */

import { useMemo, useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  Globe2,
  Library,
  RotateCw,
  Shield,
  ShieldAlert,
  Table2,
  Tags,
} from 'lucide-react';

import { ApiError } from '@/api/client';
import { useCountries, useLookupCatalog, useResourceBuMappings } from '@/api/queries';
import { useSessionScope } from '@/session/SessionScopeContext';

import { CountriesPanel } from './CountriesPanel';
import { LookupTypePanel } from './LookupTypePanel';
import { ResourceBuMappingsPanel } from './ResourceBuMappingsPanel';

/** The navigation key the server grants to Platform Admin alone. */
const NAV_KEY = 'reference-data';

/**
 * The one lookup type this screen names, and only to populate the status
 * selector on the Resource BU mapping dialog. It is named because the
 * mapping table holds a foreign key to `entity_status` in the schema -- a
 * fact about the data model, not a hard-coded tab list.
 */
const STATUS_TYPE_CODE = 'ENTITY_STATUS';

const COUNTRIES_TAB = 'countries';
const MAPPINGS_TAB = 'resource-bu-mappings';

function PageHead({ children }: { children?: ReactNode }) {
  return (
    <div className="page-head">
      <div>
        <h1 className="page-title">Reference data</h1>
        <div className="page-sub">Lookup tables, countries and resource mappings</div>
      </div>
      <div className="page-actions">
        {children ?? (
          <span className="badge pill-admin">
            <Shield aria-hidden="true" /> Admin only
          </span>
        )}
      </div>
    </div>
  );
}

function StateCard({
  icon,
  warning = false,
  title,
  children,
}: {
  icon: ReactNode;
  warning?: boolean;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="card ref-card">
      <div className="card-body">
        <div className="empty">
          <div className={`e-icon${warning ? ' e-icon-warning' : ''}`}>{icon}</div>
          <h3>{title}</h3>
          {children}
        </div>
      </div>
    </div>
  );
}

function NoAccess() {
  return (
    <>
      <PageHead />
      <StateCard warning icon={<ShieldAlert aria-hidden="true" />} title="Platform Admins only">
        <p>
          Reference data is maintained by Platform Admins. The server refuses anyone else, and
          records the refusal in the audit log.
        </p>
      </StateCard>
    </>
  );
}

/** Bars, not an empty table: distinguishable from a screen with no data. */
function LoadingSkeleton() {
  return (
    <>
      <PageHead />
      <div className="tabs ref-tabs" aria-hidden="true">
        {[0, 1, 2, 3].map((index) => (
          <span key={index} className="tab ref-tab-skeleton">
            <span className="skeleton-bar" />
          </span>
        ))}
      </div>
      <div className="card ref-card" aria-busy="true" aria-label="Loading reference data">
        <div className="card-head">
          <span className="skeleton-bar ref-skeleton-title" />
        </div>
        <div className="card-body">
          {[0, 1, 2, 3, 4].map((index) => (
            <span key={index} className="skeleton-bar ref-skeleton-line" />
          ))}
        </div>
      </div>
    </>
  );
}

export function ReferenceDataPage() {
  const { canSee } = useSessionScope();
  // Presentation only: the navigation list is the server's decision, and the
  // server refuses the API calls regardless of what renders here.
  if (!canSee(NAV_KEY)) return <NoAccess />;
  return <ReferenceDataScreen />;
}

export function ReferenceDataScreen() {
  const lookups = useLookupCatalog();
  const countries = useCountries();
  const mappings = useResourceBuMappings();
  const [activeTab, setActiveTab] = useState<string | null>(null);

  const types = useMemo(() => lookups.data?.types ?? [], [lookups.data]);

  const queries = [lookups, countries, mappings];
  const failed = queries.find((query) => query.isError);

  if (queries.some((query) => query.isPending)) return <LoadingSkeleton />;

  if (failed?.error) {
    // A 403 is a refusal, not a failure -- and it never triggers the
    // interactive re-authentication a 401 does (f1.3).
    if (failed.error instanceof ApiError && failed.error.status === 403) return <NoAccess />;
    return (
      <>
        <PageHead />
        <StateCard
          warning
          icon={<AlertTriangle aria-hidden="true" />}
          title="The reference data could not be loaded"
        >
          <p>{failed.error instanceof Error ? failed.error.message : 'Unexpected error.'}</p>
          <p className="empty-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                void lookups.refetch();
                void countries.refetch();
                void mappings.refetch();
              }}
            >
              <RotateCw aria-hidden="true" /> Retry
            </button>
          </p>
        </StateCard>
      </>
    );
  }

  const countryRows = countries.data?.countries ?? [];
  const mappingRows = mappings.data?.mappings ?? [];
  const statuses = types.find((type) => type.code === STATUS_TYPE_CODE)?.values ?? [];

  const tabs = [
    ...types.map((type) => ({ id: type.code, label: type.label, icon: Tags })),
    { id: COUNTRIES_TAB, label: 'Countries', icon: Globe2 },
    { id: MAPPINGS_TAB, label: 'Resource BU mapping', icon: Table2 },
  ];

  const current = tabs.find((tab) => tab.id === activeTab) ?? tabs[0];

  if (!current) {
    return (
      <>
        <PageHead />
        <StateCard icon={<Library aria-hidden="true" />} title="No reference data yet">
          <p>No lookup type is registered and no table has rows.</p>
        </StateCard>
      </>
    );
  }

  const activeType = types.find((type) => type.code === current.id);

  return (
    <>
      <PageHead />

      <div className="tabs ref-tabs" role="tablist" aria-label="Reference data tables">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const selected = tab.id === current.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={`ref-tab-${tab.id}`}
              aria-selected={selected}
              aria-controls={`ref-panel-${tab.id}`}
              className={`tab${selected ? ' is-active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <Icon aria-hidden="true" /> {tab.label}
            </button>
          );
        })}
      </div>

      <div
        key={current.id}
        role="tabpanel"
        id={`ref-panel-${current.id}`}
        aria-labelledby={`ref-tab-${current.id}`}
      >
        {activeType && <LookupTypePanel lookupType={activeType} />}
        {current.id === COUNTRIES_TAB && <CountriesPanel countries={countryRows} />}
        {current.id === MAPPINGS_TAB && (
          <ResourceBuMappingsPanel
            mappings={mappingRows}
            countries={countryRows}
            statuses={statuses}
          />
        )}
      </div>
    </>
  );
}
